import {
  Badge,
  Button,
  Card,
  Group,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import type {
  AdminRubricCriterion,
  DraftQuestion,
  QuestionStatus,
  UpdateDraftQuestionInput,
} from '@prepzio/shared';
import { useState } from 'react';

import { useApproveDraft, useDrafts, useRejectDraft, useUpdateDraft } from '../hooks/use-content';
import { useEnumLabel, useT } from '../i18n';
import { errorMessage } from '../lib/ui';

const STATUSES: QuestionStatus[] = ['draft', 'published', 'rejected'];

/** PRE-171 advisory threshold (mirrors JUDGE_APPROVE_THRESHOLD on the API). */
const JUDGE_APPROVE_THRESHOLD = 0.6;

/** Needs-attention first: near-duplicates, then unjudged, then lowest judge score. */
function sortForReview(drafts: DraftQuestion[]): DraftQuestion[] {
  const key = (d: DraftQuestion) => (d.duplicateOfId ? -2 : (d.judgeScore ?? -1));
  return [...drafts].sort((a, b) => key(a) - key(b));
}

export function ReviewScreen() {
  const t = useT();
  const label = useEnumLabel();
  const [status, setStatus] = useState<QuestionStatus>('draft');
  const draftsQuery = useDrafts({ status });
  const drafts = sortForReview(draftsQuery.data?.items ?? []);

  return (
    <Stack gap="md">
      <Group align="flex-end">
        <Select
          label={t('common.status')}
          data={STATUSES.map((s) => ({ value: s, label: label('status', s) }))}
          value={status}
          onChange={(v) => setStatus((v as QuestionStatus) ?? 'draft')}
          allowDeselect={false}
        />
        <Button
          variant="default"
          onClick={() => void draftsQuery.refetch()}
          loading={draftsQuery.isFetching}
        >
          {t('common.refresh')}
        </Button>
      </Group>

      {draftsQuery.isError && (
        <Text c="red" size="sm">
          {errorMessage(draftsQuery.error)}
        </Text>
      )}
      {draftsQuery.isLoading && (
        <Text c="dimmed" size="sm">
          {t('common.loading')}
        </Text>
      )}
      {!draftsQuery.isLoading && drafts.length === 0 && (
        <Text c="dimmed" size="sm">
          {t('content.nothingHere')}
        </Text>
      )}

      {drafts.map((d) => (
        <DraftCard key={d.id} draft={d} editable={status === 'draft'} />
      ))}
    </Stack>
  );
}

/** PRE-172: advisory gate outcomes (judge score, dedup) as colored chips. */
function GateBadges({ draft }: { draft: DraftQuestion }) {
  const t = useT();
  return (
    <>
      {draft.judgeScore != null && (
        <Badge
          variant="light"
          color={draft.judgeScore >= JUDGE_APPROVE_THRESHOLD ? 'green' : 'yellow'}
          title={t('review.judgeTitle')}
        >
          {t('question.judge', { score: draft.judgeScore.toFixed(2) })}
        </Badge>
      )}
      {draft.duplicateOfId != null && (
        <Badge variant="light" color="yellow" title={t('review.nearDupTitle')}>
          {t('content.nearDup')}
        </Badge>
      )}
      {draft.status === 'rejected' && draft.source === 'generated' && (
        <Badge variant="light" color="red" title={t('review.malformedTitle')}>
          {t('review.malformed')}
        </Badge>
      )}
    </>
  );
}

function DraftCard({ draft, editable }: { draft: DraftQuestion; editable: boolean }) {
  const t = useT();
  const label = useEnumLabel();
  const update = useUpdateDraft();
  const approve = useApproveDraft();
  const reject = useRejectDraft();
  const busy = update.isPending || approve.isPending || reject.isPending;

  const [prompt, setPrompt] = useState(draft.prompt);
  const [concepts, setConcepts] = useState(draft.expectedConcepts.join(', '));
  const [example, setExample] = useState(draft.exampleStrongAnswer);
  const [mistakes, setMistakes] = useState(draft.commonMistakes.join(', '));
  const [hints, setHints] = useState<string[]>(() => {
    const h = [...draft.hints];
    while (h.length < 4) h.push('');
    return h.slice(0, 4);
  });
  const [rubric, setRubric] = useState<AdminRubricCriterion[]>(draft.rubric?.criteria ?? []);
  const [rubricTitle, setRubricTitle] = useState(draft.rubric?.title ?? '');
  const [notice, setNotice] = useState<string | null>(null);

  const setHint = (i: number, v: string) =>
    setHints((cur) => cur.map((h, idx) => (idx === i ? v : h)));
  const setCriterion = (i: number, patch: Partial<AdminRubricCriterion>) =>
    setRubric((cur) => cur.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));

  const toList = (s: string) =>
    s
      .split(',')
      .map((x) => x.trim())
      .filter(Boolean);

  function onSave() {
    setNotice(null);
    const patch: UpdateDraftQuestionInput = {
      prompt,
      expectedConcepts: toList(concepts),
      exampleStrongAnswer: example,
      commonMistakes: toList(mistakes),
      hints: [hints[0]!, hints[1]!, hints[2]!, hints[3]!],
      ...(rubricTitle && rubric.length > 0
        ? { rubric: { title: rubricTitle, criteria: rubric } }
        : {}),
    };
    update.mutate({ id: draft.id, patch }, { onSuccess: () => setNotice(t('review.savedNotice')) });
  }

  const error =
    (update.isError && errorMessage(update.error)) ||
    (approve.isError && errorMessage(approve.error)) ||
    (reject.isError && errorMessage(reject.error)) ||
    null;

  const flagged = draft.duplicateOfId != null || (draft.judgeScore ?? 1) < JUDGE_APPROVE_THRESHOLD;

  return (
    <Card
      withBorder
      padding="lg"
      radius="md"
      style={flagged ? { borderColor: 'var(--mantine-color-yellow-6)' } : undefined}
    >
      <Group gap="xs">
        <Badge variant="light" color="gray">
          {label('type', draft.type)}
        </Badge>
        <Badge variant="light" color="gray">
          {label('difficulty', draft.difficulty)}
        </Badge>
        <Badge variant="light" color="gray">
          {label('status', draft.status)}
        </Badge>
        <GateBadges draft={draft} />
      </Group>
      {draft.duplicateOfId != null && (
        <Text c="dimmed" size="sm" mt={6}>
          {t('review.nearDupNote', { id: draft.duplicateOfId })}
        </Text>
      )}
      {draft.judgeRationale && (
        <Text c="dimmed" size="sm" mt={4}>
          {t('question.judgeRationale', { rationale: draft.judgeRationale })}
        </Text>
      )}

      {editable ? (
        <Stack gap="sm" mt="sm">
          <Textarea
            label={t('question.prompt')}
            autosize
            minRows={2}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
          />
          <TextInput
            label={t('review.conceptsLabel')}
            value={concepts}
            onChange={(e) => setConcepts(e.target.value)}
          />
          <Textarea
            label={t('question.exampleStrongAnswer')}
            autosize
            minRows={2}
            value={example}
            onChange={(e) => setExample(e.target.value)}
          />
          <TextInput
            label={t('review.mistakesLabel')}
            value={mistakes}
            onChange={(e) => setMistakes(e.target.value)}
          />
          <Stack gap="xs">
            <Text size="sm" fw={500}>
              {t('review.hintLadderLabel')}
            </Text>
            {hints.map((h, i) => (
              <TextInput
                key={i}
                value={h}
                placeholder={t('review.hintPlaceholder', { n: i + 1 })}
                onChange={(e) => setHint(i, e.target.value)}
              />
            ))}
          </Stack>
          {rubric.length > 0 && (
            <Stack gap="xs">
              <Text size="sm" fw={500}>
                {t('review.rubricHeading')}
              </Text>
              <TextInput
                value={rubricTitle}
                placeholder={t('review.rubricTitlePlaceholder')}
                onChange={(e) => setRubricTitle(e.target.value)}
              />
              {rubric.map((c, i) => (
                <SimpleGrid key={c.key ?? i} cols={{ base: 1, sm: 3 }} spacing="xs">
                  <TextInput
                    value={c.label}
                    placeholder={t('review.criterionLabel')}
                    onChange={(e) => setCriterion(i, { label: e.target.value })}
                  />
                  <TextInput
                    value={c.description}
                    placeholder={t('question.description')}
                    onChange={(e) => setCriterion(i, { description: e.target.value })}
                  />
                  <NumberInput
                    min={1}
                    max={5}
                    value={c.maxScore}
                    onChange={(v) =>
                      setCriterion(i, {
                        maxScore: Math.min(5, Math.max(1, Number(v) || 1)),
                      })
                    }
                  />
                </SimpleGrid>
              ))}
            </Stack>
          )}
          <Group>
            <Button variant="default" onClick={onSave} loading={busy}>
              {t('review.saveChanges')}
            </Button>
            <Button onClick={() => approve.mutate(draft.id)} disabled={busy}>
              {t('review.approve')}
            </Button>
            <Button color="red" onClick={() => reject.mutate(draft.id)} disabled={busy}>
              {t('review.reject')}
            </Button>
          </Group>
        </Stack>
      ) : (
        <>
          <Text mt="sm">{draft.prompt}</Text>
          <Text c="dimmed" size="sm">
            {t('content.expected', { concepts: draft.expectedConcepts.join(', ') })}
          </Text>
        </>
      )}

      {error && (
        <Text c="red" size="sm" mt="sm">
          {error}
        </Text>
      )}
      {!error && notice && (
        <Text c="dimmed" size="sm" mt="sm">
          {notice}
        </Text>
      )}
    </Card>
  );
}
